import { useState, type ReactNode } from 'react';
import type { CorridorException } from '@ave/shared';
import { api } from '../lib/api';
import { dateTime, pct } from '../lib/format';
import { Badge, Card, EmptyState, SectionHeading } from './ui';
import { Icon } from './icons';

/**
 * Corridor exceptions.
 *
 * The valuer's judgement is allowed to leave the corridor. What it cannot
 * do is leave it silently: an out-of-corridor assumption blocks the stage
 * gate until a reviewer approves it with a reason, and a material one
 * then renders in the report. This panel is where that conversation
 * happens.
 */
export function ExceptionsPanel({
  exceptions,
  canApprove,
  onChange,
  footer,
}: {
  exceptions: CorridorException[];
  canApprove: boolean;
  onChange: () => void;
  /** Where keying a new assumption starts, next to what keying one produced. */
  footer?: ReactNode;
}) {
  const pending = exceptions.filter((e) => e.status === 'pending');
  const settled = exceptions.filter((e) => e.status !== 'pending');

  return (
    <Card className={pending.length > 0 ? 'border-amber-300' : ''}>
      <SectionHeading
        title="Assumption corridors"
        subtitle={
          pending.length > 0
            ? `${pending.length} assumption${pending.length === 1 ? '' : 's'} outside corridor — blocking the stage gate`
            : 'Every keyed assumption is checked against the corridor in force at the valuation date'
        }
        right={
          pending.length > 0 ? (
            <Badge tone="amber">{pending.length} blocking</Badge>
          ) : (
            <Badge tone="emerald">Clear</Badge>
          )
        }
      />

      {exceptions.length === 0 ? (
        <EmptyState
          title="No exceptions"
          detail="Every assumption keyed on this engagement sits inside its corridor."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {[...pending, ...settled].map((e) => (
            <ExceptionRow
              key={e.id}
              exception={e}
              canApprove={canApprove}
              onChange={onChange}
            />
          ))}
        </ul>
      )}
      {footer ? <div className="border-t border-slate-100 px-6 py-3">{footer}</div> : null}
    </Card>
  );
}

function ExceptionRow({
  exception,
  canApprove,
  onChange,
}: {
  exception: CorridorException;
  canApprove: boolean;
  onChange: () => void;
}) {
  const [justification, setJustification] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isPending = exception.status === 'pending';

  const submit = async (decision: 'approve' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      if (decision === 'approve') await api.approveException(exception.id, justification);
      else await api.rejectException(exception.id, justification);
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const { min, max } = exception.corridorBounds;
  const value = Number(exception.enteredValue);
  const above = max !== null && value > max;

  return (
    // Tagged so a blocking check at Stage 7 or 9 can bring the reviewer
    // straight to the exception it is complaining about.
    <li data-item-id={exception.id} className="scroll-mt-24 rounded-lg px-6 py-4">
      <div className="flex items-start gap-3">
        <Icon
          name={isPending ? 'alert' : exception.status === 'approved' ? 'checkCircle' : 'x'}
          className={`mt-0.5 h-5 w-5 shrink-0 ${
            isPending
              ? 'text-amber-500'
              : exception.status === 'approved'
                ? 'text-emerald-500'
                : 'text-rose-500'
          }`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <p className="font-mono text-sm text-slate-800">{exception.assumptionKey}</p>
            {exception.material ? <Badge tone="slate">Material</Badge> : null}
            {!isPending ? (
              <Badge tone={exception.status === 'approved' ? 'emerald' : 'rose'}>
                {exception.status === 'approved' ? 'Approved' : 'Rejected'}
              </Badge>
            ) : null}
          </div>

          <p className="mt-1 text-sm text-slate-600">
            Keyed at <span className="tnum font-semibold text-slate-900">{pct(value)}</span>,{' '}
            {above ? 'above' : 'below'} the corridor of{' '}
            <span className="tnum">
              {min !== null ? pct(min) : '—'} to {max !== null ? pct(max) : '—'}
            </span>
            .
          </p>

          <CorridorScale min={min} max={max} value={value} />

          {exception.justification ? (
            <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {exception.justification}
            </p>
          ) : null}

          {!isPending && exception.approvedAt ? (
            <p className="mt-1.5 text-xs text-slate-400">
              Decided {dateTime(exception.approvedAt)}
            </p>
          ) : null}

          {isPending ? (
            canApprove ? (
              <div className="mt-3">
                <textarea
                  value={justification}
                  onChange={(ev) => setJustification(ev.target.value)}
                  rows={2}
                  placeholder="Why is this assumption right for this engagement? This text renders in the report where material."
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={busy || justification.trim().length < 10}
                    onClick={() => submit('approve')}
                  >
                    Approve exception
                  </button>
                  <button
                    type="button"
                    className="btn-danger text-xs"
                    disabled={busy || justification.trim().length < 3}
                    onClick={() => submit('reject')}
                  >
                    Reject
                  </button>
                  {justification.trim().length < 10 ? (
                    <span className="text-xs text-slate-400">
                      A justification is required — it is recorded against your approval.
                    </span>
                  ) : null}
                </div>
                {error ? <p className="mt-2 text-xs text-rose-600">{error}</p> : null}
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                Awaiting a reviewer. Your role cannot approve corridor exceptions.
              </p>
            )
          ) : null}
        </div>
      </div>
    </li>
  );
}

/** Shows where the keyed value sits relative to the permitted band. */
function CorridorScale({
  min,
  max,
  value,
}: {
  min: number | null;
  max: number | null;
  value: number;
}) {
  if (min === null || max === null) return null;
  const span = max - min;
  const lo = min - span * 0.6;
  const hi = max + span * 0.6;
  const at = (v: number) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));

  return (
    <div className="mt-2.5">
      <div className="relative h-1.5 w-full rounded-full bg-slate-100">
        <div
          className="absolute h-1.5 rounded-full bg-teal-200"
          style={{ left: `${at(min)}%`, width: `${at(max) - at(min)}%` }}
        />
        <div
          className="absolute -top-1 h-3.5 w-0.5 rounded-full bg-amber-500"
          style={{ left: `${at(value)}%` }}
          title={`Keyed at ${(value * 100).toFixed(1)}%`}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-400">
        <span className="tnum">{(min * 100).toFixed(0)}%</span>
        <span>corridor</span>
        <span className="tnum">{(max * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}
